import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, type Campaign } from "../api/client";
import { EmptyMessage } from "../components/EmptyMessage";
import { PageHeader } from "../components/PageHeader";
import { formatShortDate, formatTimeParts } from "../lib/format";
import type { SurveySubmission } from "./SurveyDetailPage";

type LogRow = {
  id: string;
  kind: "campaign" | "survey" | "reminder";
  patient: string;
  communication: string;
  type: string;
  sent: string;
  provider: string;
  appointmentDate: string;
  href?: string;
};

/**
 * Communications activity log (`communications/activity-log.html`).
 * Synthetic: campaigns + survey submissions as a single table.
 */
export function ActivityLogPage() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "campaign" | "survey" | "reminder">(
    "all",
  );
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiGet<{ campaigns: Campaign[] }>("/campaigns"),
      apiGet<Array<{ submissions: SurveySubmission[] }>>("/survey_submissions"),
    ])
      .then(([campData, surveyData]) => {
        if (cancelled) return;
        const campaigns = campData?.campaigns ?? [];
        const submissions = surveyData?.[0]?.submissions ?? [];
        const next: LogRow[] = [
          ...campaigns.map((c) => ({
            id: c.id,
            kind: "campaign" as const,
            patient: "—",
            communication: c.name,
            type: "Campaign",
            sent: c.sent_at || c.updated_at || c.created_at || "",
            provider: "—",
            appointmentDate: "",
            href: `/app/campaigns/${c.id}/edit/email`,
          })),
          ...submissions.map((s) => ({
            id: s.id,
            kind: "survey" as const,
            patient: s.appointment?.patient_name || s.patient_name || "—",
            communication: s.title,
            type: "Survey",
            sent: s.updated_at || s.created_at || "",
            provider: s.appointment?.provider_name || s.provider_name || "—",
            appointmentDate: s.updated_at || s.created_at || "",
            href: `/app/surveys/${s.id}`,
          })),
        ];
        next.sort(
          (a, b) => new Date(b.sent).getTime() - new Date(a.sent).getTime(),
        );
        setRows(next);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => {
    return {
      campaign: rows.filter((r) => r.kind === "campaign").length,
      survey: rows.filter((r) => r.kind === "survey").length,
      reminder: 0,
    };
  }, [rows]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && r.kind !== filter) return false;
      if (!term) return true;
      return (
        r.patient.toLowerCase().includes(term) ||
        r.communication.toLowerCase().includes(term) ||
        r.provider.toLowerCase().includes(term)
      );
    });
  }, [rows, filter, search]);

  return (
    <>
      <PageHeader title="Activity Log" />
      <div className="ne__activity-log" style={{ padding: "0 5% 5vw" }}>
        <div className="ne__activity-log__table">
          <div
            className="ne__table__filters"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <fieldset className="ne__search ne__search--expandable">
              <label>
                <i className="ne__icon__search" />
                <input
                  className="ne__search__input"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search activity log"
                />
              </label>
            </fieldset>
            <div className="ne__btn__group">
              {(
                [
                  ["campaign", "Campaigns", counts.campaign],
                  ["survey", "Surveys", counts.survey],
                  ["reminder", "Reminders", counts.reminder],
                ] as const
              ).map(([key, label, count]) => (
                <button
                  key={key}
                  type="button"
                  className={`ne__btn ne__btn--tertiary ne__btn--md${
                    filter === key ? " active" : ""
                  }`}
                  onClick={() =>
                    setFilter((f) => (f === key ? "all" : key))
                  }
                >
                  {label}
                  <span className="ne__badge ne__badge--num">{count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <EmptyMessage>
            <p>Loading activity log…</p>
          </EmptyMessage>
        ) : null}

        {!loading ? (
          <div className="ne__table__padding">
            <table className="ne__table ne__table--icon">
              <thead>
                <tr>
                  <th />
                  <th className="ne__table__eyebrow">Patient</th>
                  <th className="ne__table__eyebrow">Communication</th>
                  <th className="ne__table__eyebrow">Type</th>
                  <th className="ne__table__eyebrow">Sent</th>
                  <th className="ne__table__eyebrow">Provider</th>
                  <th className="ne__table__eyebrow">Appointment Date</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const { time, period } = formatTimeParts(row.sent);
                  const cell = (
                    <>
                      <td>
                        <i
                          className={
                            row.kind === "survey"
                              ? "ne__icon__forms--thinnest"
                              : "ne__icon__megaphone--thinnest"
                          }
                        />
                      </td>
                      <td>{row.patient}</td>
                      <td>{row.communication}</td>
                      <td>{row.type}</td>
                      <td>
                        {row.sent ? formatShortDate(row.sent) : "—"}
                        {time ? (
                          <>
                            {" "}
                            {time}
                            <span className="ne__period"> {period}</span>
                          </>
                        ) : null}
                      </td>
                      <td>{row.provider}</td>
                      <td>
                        {row.appointmentDate
                          ? formatShortDate(row.appointmentDate)
                          : "—"}
                      </td>
                    </>
                  );
                  return row.href ? (
                    <tr key={row.id}>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <Link
                          to={row.href}
                          style={{
                            display: "table",
                            width: "100%",
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          <table style={{ width: "100%" }}>
                            <tbody>
                              <tr>{cell}</tr>
                            </tbody>
                          </table>
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    <tr key={row.id}>{cell}</tr>
                  );
                })}
              </tbody>
            </table>
            {visible.length === 0 ? (
              <EmptyMessage title="No activity matches this filter." />
            ) : null}
          </div>
        ) : null}

        <p style={{ marginTop: "2rem" }}>
          <Link to="/app/communications">← Back to communications</Link>
        </p>
      </div>
    </>
  );
}
